import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid2, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AlertListItemIncident from "./AlertListItemIncident";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SeverityBadge from "./Badges/SeverityBadge";
import CustomCheckbox from "./Components/CustomCheckbox";
import MoreDetailsDialog from "./Components/MoreDetailsDialog";
import MapInfoDialog from "./Components/MapInfoDialog";
import { encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import { getBatteryAlertIncidents } from "../../Api/BmsFaultsApi";
import CartLoader from "../../Helper/Loaders/CartLoader";

const AlertListItem = ({ battery, expandAll, selectedTab, selectedBatteries, onSelect, onActionClick, globalFilters, incidentFilters, onIncidentsFetched, fetchIncidentsFn, showActions = true, showAnalyse = true, isPlatformAlert = false, analyseInfoFn, overdueThreshold = 7, defaultAlertsSubTab }) => {

    const navigate = useNavigate();
    const {licensePlateNo, severityStats = []} = battery;
    const isSelected = selectedBatteries.has(battery.batteryID);

    const [expanded, setExpanded] = useState(false);
    const [alertIncidents, setAlertIncidents] = useState([]);
    const [isLoadingIncidents, setIsLoadingIncidents] = useState(false);
    const hasFetchedRef = useRef(false);

    const fetchIncidents = async () => {
        if (hasFetchedRef.current) return;

        setIsLoadingIncidents(true);
        try {
            const fetchFn = fetchIncidentsFn || getBatteryAlertIncidents;
            const incidentsRes = await fetchFn(
                globalFilters,
                battery.batteryID,
                incidentFilters
            );
            const incidentsList = incidentsRes?.response?.data || [];
            setAlertIncidents(incidentsList);
            hasFetchedRef.current = true;
            // Update parent cache with fetched incidents
            onIncidentsFetched?.(battery.batteryID, incidentsList);
        } catch (error) {
            console.error(`Error fetching alerts for ${battery.batteryID}`, error);
            setAlertIncidents([]);
        } finally {
            setIsLoadingIncidents(false);
        }
    };

    const handleAccordionChange = () => {
        const willExpand = !expanded;
        setExpanded(willExpand);
        if (willExpand && !hasFetchedRef.current) {
            fetchIncidents();
        }
    };

    const handleActionClick = (alertIncident) => {
        onActionClick(battery, alertIncident);
    }

    const handleBatteryIdClick = (e) => {
        e.stopPropagation();
        const temp = {
            batteryID: battery.batteryID,
            tabValue: "alerts",
            navigateFromTab: true,
            ...(defaultAlertsSubTab && { alertsSubTab: defaultAlertsSubTab }),
        };
        encryptTheParams(temp, navigate, true, '', true);
    }

    useEffect(() => {
        setExpanded(expandAll);
        if (expandAll && !hasFetchedRef.current) {
            fetchIncidents();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandAll]);

    // Reset fetch state when filters change so incidents are refetched on next expand
    useEffect(() => {
        hasFetchedRef.current = false;
        setAlertIncidents([]);
        setIsLoadingIncidents(false);
        if (expanded) {
            fetchIncidents();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incidentFilters, globalFilters]);

    return (
        <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            disableGutters
            elevation={0}
            square
            sx={{
                boxShadow: '0px 4px 10px 0px rgba(151, 151, 151, 0.2)',
                borderRadius: '8px',
                "&:before": { display: "none" },
                "&.Mui-disabled": {
                    backgroundColor: '#FFFFFF',
                },
                "&:hover": {
                    backgroundColor: '#FBFBFF'
                }
            }}
            >
            <AccordionSummary
                sx={{
                    padding: expanded ? '20px 24px 20px 24px' : '20px 24px 20px 24px',
                    "& .MuiAccordionSummary-content": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        m: 0,
                    },
                }}
            >
                <Box display="flex" alignItems="center" justifyContent={'space-between'} gap={1} width={'100%'}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2}}>
                        <Box
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()} // for keyboard users (accessibility)
                        >
                            <CustomCheckbox
                                variant="light"
                                checked={isSelected}
                                onChange={(e) => onSelect(battery, e.target.checked)}
                            />
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <FiberManualRecordIcon sx={{ color: battery?.pingStatus?.toLowerCase() === 'active' ? "#09B820" : "#8F8F8F" }} />
                            <Typography
                                variant="fw400s14lh24c0061CA"
                                sx={{ fontWeight: 500, fontSize: '18px', '&:hover': {textDecoration: 'underline'} }}
                                onClick={handleBatteryIdClick}
                                onFocus={(e) => e.stopPropagation()}
                            >
                                {licensePlateNo}
                            </Typography>
                        </Box>
                        {battery?.latitude && battery.longitude && (
                            <MapInfoDialog battery={battery} />
                        )}
                        <MoreDetailsDialog battery={battery} />
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                        {!expanded && severityStats.length > 0 &&
                            severityStats.map((severity, key) => (
                                <SeverityBadge
                                    key={key}
                                    severity={severity.severityName}
                                    count={severity.count}
                                />
                            ))
                        }
                        <ExpandMoreIcon
                            fontSize="large"
                            sx={{
                            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                            "&:hover": {
                                color: '#4D44D8',
                            },
                            color: '#000000',
                            }}
                        />
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, pb: 2, px: 3 }}>
                <Divider orientation="horizontal" flexItem sx={{ borderColor: "#E4E4E4", mb: 2 }} />
                <Grid2 container flexDirection={'column'} gap={1}>
                    {isLoadingIncidents ? (
                        Array.from({ length: severityStats.reduce((sum, s) => sum + (s.count || 0), 0) || 1 }).map((_, index) => (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'center', height: '102px' }}>
                                <CartLoader />
                            </Box>
                        ))
                    ) : (
                        alertIncidents.map((alertIncident, index) => (
                            <AlertListItemIncident
                                key={alertIncident.incidentID || index}
                                alertIncident={alertIncident}
                                selectedTab={selectedTab}
                                battery={battery}
                                onActionClick={() => handleActionClick(alertIncident)}
                                showActions={showActions}
                                showAnalyse={showAnalyse}
                                isPlatformAlert={isPlatformAlert}
                                analyseInfoFn={analyseInfoFn}
                                overdueThreshold={overdueThreshold}
                            />
                        ))
                    )}
                </Grid2>
            </AccordionDetails>
        </Accordion>
    );
};

export default AlertListItem;
